import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { searchWallet, SearchWalletRequest, getWalletDetail } from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";
import {
  connectXero,
  createBankAccount,
  CreateBankAccountRequest,
  createXeroTokenForOrganisation,
  disconnectXero,
  getOrganisationXeroAccounts,
  getOrganisationXeroTaxRate,
  getXeroTrackingCategories,
  syncOrganisationTransactionsWithXero,
  SyncOrganisationTransactionsWithXeroRequest
} from "src/network/graphql/xeroServices";
import {
  saveOrganisationPreferences,
  SaveOrganisationPreferencesRequest
} from "src/network/graphql/organisationService";
import { immediateToast } from "izitoast-react";
import { convertToCSV } from "src/utils";
import { getAllTransaction, TransactionRequest } from "src/network/graphql/transactionService";
import { EMPTY_STRING, NOVATTI_CARD_TRANSACTION } from "src/components/constant/constant";
import { xeroMetaApi } from "./xeroMetaApi";

export type XeroAccount = {
  accountId: string;
  code: string;
  name: string;
  description: string;
  taxType: string;
  type?: string;
};

export type XeroTrackingCategories = {
  trackingCategoryID: string;
  name: string;
  options: {
    trackingOptionID: string;
    name: string;
  }[];
};

export type XeroTaxType = {
  taxType: string;
  name: string;
};

const initialWallets: Wallet[] = [];

const xeroSlice = createSlice({
  name: "xero",
  initialState: {
    loading: false,
    xeroWalletLoading: false,
    createBankAccountLoading: false,
    error: null,
    xeroWalletList: {
      list: initialWallets,
      total: 0
    },
    downloadXeroAccountLoading: false,
    downloadXeroTrackingCategoriesLoading: false,
    downloadXeroTaxRatesLoading: false,
    syncOrganisationTransactionsWithXeroLoading: false,
    disconnectXeroLoading: false,
    xeroActivitySearchLoading: false,
    xeroActivitySearch: {
      totalTransaction: 0,
      qualifiedTransactions: 0,
      qualifiedSyncedTransaction: 0,
      qualifiedNonSyncedTransaction: 0
    },
    saveOrganisationPreferencesLoading: false
  },
  reducers: {
    fetchUpdateOrgXeroStart: (state) => {
      state.loading = true;
    },
    fetchUpdateOrgXeroSuccess: (state) => {
      state.loading = false;
    },
    fetchUpdateOrgXeroFail: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    fetchXeroWalletListStart: (state) => {
      state.xeroWalletLoading = true;
    },
    fetchXeroWalletListSuccess: (state, action) => {
      state.xeroWalletLoading = false;
      state.xeroWalletList.list = action.payload?.list ?? [];
      state.xeroWalletList.total = action.payload?.total ?? 0;
    },
    fetchXeroWalletListFail: (state, action) => {
      state.xeroWalletLoading = false;
      state.xeroWalletList.list = [];
      state.xeroWalletList.total = 0;
      state.error = action.payload;
    },
    downloadXeroAccountStart: (state) => {
      state.downloadXeroAccountLoading = true;
    },
    downloadXeroAccountSuccess: (state) => {
      state.downloadXeroAccountLoading = false;
    },
    downloadXeroAccountFail: (state, action) => {
      state.downloadXeroAccountLoading = false;
      state.error = action.payload;
    },
    downloadXeroTrackingCategoriesStart: (state) => {
      state.downloadXeroTrackingCategoriesLoading = true;
    },
    downloadXeroTrackingCategoriesSuccess: (state) => {
      state.downloadXeroTrackingCategoriesLoading = false;
    },
    downloadXeroTrackingCategoriesFail: (state, action) => {
      state.downloadXeroTrackingCategoriesLoading = false;
      state.error = action.payload;
    },
    downloadXeroTaxRatesStart: (state) => {
      state.downloadXeroTaxRatesLoading = true;
    },
    downloadXeroTaxRatesSuccess: (state) => {
      state.downloadXeroTaxRatesLoading = false;
    },
    downloadXeroTaxRatesFail: (state, action) => {
      state.downloadXeroTaxRatesLoading = false;
      state.error = action.payload;
    },
    createBankAccountStart: (state) => {
      state.createBankAccountLoading = true;
    },
    createBankAccountSuccess: (state) => {
      state.createBankAccountLoading = false;
    },
    createBankAccountFail: (state, action) => {
      state.createBankAccountLoading = false;
      state.error = action.payload;
    },
    syncOrganisationTransactionsWithXeroStart: (state) => {
      state.syncOrganisationTransactionsWithXeroLoading = true;
    },
    syncOrganisationTransactionsWithXeroSuccess: (state) => {
      state.syncOrganisationTransactionsWithXeroLoading = false;
    },
    syncOrganisationTransactionsWithXeroFail: (state, action) => {
      state.syncOrganisationTransactionsWithXeroLoading = false;
      state.error = action.payload;
    },
    disconnectXeroStart: (state) => {
      state.disconnectXeroLoading = true;
    },
    disconnectXeroSuccess: (state) => {
      state.disconnectXeroLoading = false;
    },
    disconnectXeroFail: (state, action) => {
      state.disconnectXeroLoading = false;
      state.error = action.payload;
    },
    xeroActivitySearchStart: (state) => {
      state.xeroActivitySearchLoading = true;
    },
    xeroActivitySearchSuccess: (state, action) => {
      state.xeroActivitySearchLoading = false;
      state.xeroActivitySearch = action.payload;
    },
    xeroActivitySearchFail: (state, action) => {
      state.xeroActivitySearchLoading = false;
      state.xeroActivitySearch = {
        totalTransaction: 0,
        qualifiedTransactions: 0,
        qualifiedSyncedTransaction: 0,
        qualifiedNonSyncedTransaction: 0
      };
      state.error = action.payload;
    },
    saveOrganisationPreferencesStart: (state) => {
      state.saveOrganisationPreferencesLoading = true;
    },
    saveOrganisationPreferencesSuccess: (state) => {
      state.saveOrganisationPreferencesLoading = false;
    },
    saveOrganisationPreferencesFail: (state, action) => {
      state.saveOrganisationPreferencesLoading = false;
      state.error = action.payload;
    },
    updateXeroWalletRow: (
      state,
      action: {
        payload: {
          walletId: string;
          preferences: {
            xero_account_id?: string;
            xero_tax_type?: string;
            xero_tracking_category_mapping?: { xero_tracking_category_id: string; xero_tracking_option_id?: string }[];
          };
        };
      }
    ) => {
      const { walletId, preferences } = action.payload;
      const idx = state.xeroWalletList.list.findIndex((w) => String(w.id) === walletId);

      if (idx !== -1) {
        const wallet = state.xeroWalletList.list[idx] as Record<string, unknown>;
        const existing = (wallet.preferences ?? {}) as Record<string, unknown>;

        wallet.preferences = { ...existing, ...preferences };
      }
    }
  }
});

const {
  fetchUpdateOrgXeroFail,
  fetchUpdateOrgXeroStart,
  fetchUpdateOrgXeroSuccess,
  fetchXeroWalletListStart,
  fetchXeroWalletListSuccess,
  fetchXeroWalletListFail,
  downloadXeroAccountStart,
  downloadXeroAccountSuccess,
  downloadXeroAccountFail,
  downloadXeroTrackingCategoriesFail,
  downloadXeroTrackingCategoriesStart,
  downloadXeroTrackingCategoriesSuccess,
  downloadXeroTaxRatesStart,
  downloadXeroTaxRatesSuccess,
  downloadXeroTaxRatesFail,
  createBankAccountStart,
  createBankAccountFail,
  createBankAccountSuccess,
  syncOrganisationTransactionsWithXeroFail,
  syncOrganisationTransactionsWithXeroStart,
  syncOrganisationTransactionsWithXeroSuccess,
  disconnectXeroFail,
  disconnectXeroStart,
  disconnectXeroSuccess,
  xeroActivitySearchStart,
  xeroActivitySearchSuccess,
  xeroActivitySearchFail,
  saveOrganisationPreferencesStart,
  saveOrganisationPreferencesSuccess,
  updateXeroWalletRow,
  saveOrganisationPreferencesFail
} = xeroSlice.actions;

export const updateOrgForXero = (url: string, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUpdateOrgXeroStart());
    try {
      await createXeroTokenForOrganisation(url);

      dispatch(fetchUpdateOrgXeroSuccess());
      dispatch(xeroMetaApi.util.resetApiState());
      if (cb) cb();
    } catch (err) {
      if (fcb) fcb();
      dispatch(fetchUpdateOrgXeroFail(err));
    }
  };
};

export const connectXeroToOrganisation = (
  callback_url: string,
  cb?: () => void,
  fcb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUpdateOrgXeroStart());
    try {
      await connectXero(callback_url);

      dispatch(fetchUpdateOrgXeroSuccess());
      if (cb) cb();
    } catch (err) {
      if (fcb) fcb();
      dispatch(fetchUpdateOrgXeroFail(err));
    }
  };
};

export const fetchXeroWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchXeroWalletListStart());
    try {
      const response = await searchWallet(data);
      const wallets = response?.data?.search_wallet?.items ?? [];
      const structuredResponse = await Promise.all(
        wallets.map(async (wallet: any) => {
          let preferences = wallet?.preference_attributes;

          if (!preferences) {
            const detailResp = await getWalletDetail(wallet.wallet_id);

            preferences = detailResp?.data?.get_wallet?.preferences;
          }

          return {
            id: wallet.wallet_id,
            description: wallet.description,
            status: wallet.wallet_status,
            name: wallet.name,
            walletType: wallet.wallet_type,
            preferences: {
              cashWithdrawalAllowed: preferences?.is_cash_withdrawal_allowed,
              maxAllowedLimit: preferences?.maximum_allowed_limit,
              preferenceId: preferences?.preference_id,
              spendFrequency: preferences?.spend_frequency,
              spendLimit: preferences?.spend_limit,
              blockedMerchants: preferences?.blocked_merchant_ids,
              blockedMccCategories: preferences?.blocked_mcc_categories,
              isTransferAllowed: preferences?.is_transfer_allowed,
              xero_account_id: preferences?.xero_account_id,
              xero_tax_type: preferences?.xero_tax_type,
              xero_tracking_category_mapping: preferences?.xero_tracking_category_mapping ?? []
            }
          };
        })
      );

      dispatch(
        fetchXeroWalletListSuccess({
          list: structuredResponse,
          total: response?.data?.search_wallet?.total_size
        })
      );
    } catch (error) {
      dispatch(fetchXeroWalletListFail(error));
    }
  };
};

export const downloadXeroAccountListAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadXeroAccountStart());
    try {
      const response = await getOrganisationXeroAccounts();
      const data = response.data.get_organisation_xero_accounts;

      if (data && Array.isArray(data) && data.length > 0) {
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "xero_accounts.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        immediateToast("success", { message: "Xero account CSV downloaded.", timeout: 3000, position: "topCenter" });
        dispatch(downloadXeroAccountSuccess());
        return true;
      } else {
        immediateToast("error", { message: "No Xero account data to download.", timeout: 3000, position: "topCenter" });
        dispatch(downloadXeroAccountFail("No data"));
        return false;
      }
    } catch (err) {
      immediateToast("error", { message: "Failed to download Xero accounts.", timeout: 3000, position: "topCenter" });
      dispatch(downloadXeroAccountFail(err));
      return false;
    }
  };
};

export const downloadXeroTrackingCategoriesAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadXeroTrackingCategoriesStart());
    try {
      const response = await getXeroTrackingCategories();

      const data = response.data.get_organisation_xero_categories;

      if (data && Array.isArray(data) && data.length > 0) {
        const rows: Array<Record<string, string>> = [];

        data.forEach((cat) => {
          const catId = cat.trackingCategoryID ?? EMPTY_STRING;
          const catName = cat.name ?? EMPTY_STRING;

          if (Array.isArray(cat.options) && cat.options.length > 0) {
            cat.options.forEach((opt: any) => {
              rows.push({
                tracking_category_id: catId,
                tracking_category_name: catName,
                tracking_option_id: opt.trackingOptionID ?? EMPTY_STRING,
                tracking_option_name: opt.name ?? EMPTY_STRING
              });
            });
          } else {
            rows.push({
              tracking_category_id: catId,
              tracking_category_name: catName,
              tracking_option_id: EMPTY_STRING,
              tracking_option_name: EMPTY_STRING
            });
          }
        });

        const csv = convertToCSV(rows);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "xero_categories.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        immediateToast("success", { message: "Xero account CSV downloaded.", timeout: 3000, position: "topCenter" });
        dispatch(downloadXeroTrackingCategoriesSuccess());
        return true;
      } else {
        immediateToast("error", { message: "No Xero account data to download.", timeout: 3000, position: "topCenter" });
        dispatch(downloadXeroTrackingCategoriesFail("No data"));
        return false;
      }
    } catch (err) {
      immediateToast("error", { message: "Failed to download Xero accounts.", timeout: 3000, position: "topCenter" });
      dispatch(downloadXeroTrackingCategoriesFail(err));
      return false;
    }
  };
};

export const downloadXeroTaxRatesAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadXeroTaxRatesStart());
    try {
      const response = await getOrganisationXeroTaxRate();
      const data = response?.data?.get_organisation_xero_tax_rate ?? [];

      if (Array.isArray(data) && data.length > 0) {
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "xero_tax_rates.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        immediateToast("success", { message: "Xero tax rates CSV downloaded.", timeout: 3000, position: "topCenter" });
        dispatch(downloadXeroTaxRatesSuccess());
        return true;
      }
      immediateToast("error", { message: "No Xero tax rates data to download.", timeout: 3000, position: "topCenter" });
      dispatch(downloadXeroTaxRatesFail("No data"));
      return false;
    } catch (err) {
      immediateToast("error", { message: "Failed to download Xero tax rates.", timeout: 3000, position: "topCenter" });
      dispatch(downloadXeroTaxRatesFail(err));
      return false;
    }
  };
};

export const createBankAccountAction = (data: CreateBankAccountRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createBankAccountStart());
    try {
      await createBankAccount(data);
      immediateToast("success", {
        message: "Bank Account Created Successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(createBankAccountSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(createBankAccountFail(error));
    }
  };
};

export const syncOrganisationTransactionsWithXeroAction = (
  data: SyncOrganisationTransactionsWithXeroRequest,
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(syncOrganisationTransactionsWithXeroStart());
    try {
      await syncOrganisationTransactionsWithXero(data);
      immediateToast("success", {
        message: "Transaction Synced Successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(syncOrganisationTransactionsWithXeroSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(syncOrganisationTransactionsWithXeroFail(error));
    }
  };
};

export const disconnectXeroAction = (cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(disconnectXeroStart());
    try {
      await disconnectXero();
      immediateToast("success", {
        message: "Xero disconnected successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(disconnectXeroSuccess());
      dispatch(xeroMetaApi.util.resetApiState());
      if (cb) cb();
    } catch (error) {
      dispatch(disconnectXeroFail(error));
    }
  };
};

export const fetchXeroActivitySummary = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(xeroActivitySearchStart());
    const activityField = {
      total_pages: true,
      total_size: true
    };

    try {
      const totalTrans = await getAllTransaction(
        {
          filter: {
            ...data.filter,
            activityStatus: "SETTLED",
            activityName: NOVATTI_CARD_TRANSACTION
          }
        },
        activityField
      );
      const qualifiedTrans = await getAllTransaction(
        {
          filter: {
            ...data.filter,
            activityStatus: "SETTLED",
            xero_account_exist: true,
            activityName: NOVATTI_CARD_TRANSACTION
          }
        },
        activityField
      );
      const qualifiedSyncedTrans = await getAllTransaction(
        {
          filter: {
            ...data.filter,
            activityStatus: "SETTLED",
            // xero_account_exist: true,
            xero_bank_transaction_exist: true,
            activityName: NOVATTI_CARD_TRANSACTION
          }
        },
        activityField
      );

      dispatch(
        xeroActivitySearchSuccess({
          totalTransaction: totalTrans.data.search_activity.total_size,
          qualifiedTransactions: qualifiedTrans.data.search_activity.total_size,
          qualifiedSyncedTransaction: qualifiedSyncedTrans.data.search_activity.total_size,
          qualifiedNonSyncedTransaction:
            qualifiedTrans.data.search_activity.total_size - qualifiedSyncedTrans.data.search_activity.total_size
        })
      );
    } catch (error) {
      dispatch(xeroActivitySearchFail(error));
    }
  };
};

export const saveOrganisationPreferencesAction = (data: SaveOrganisationPreferencesRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveOrganisationPreferencesStart());
    try {
      await saveOrganisationPreferences(data);
      immediateToast("success", {
        message: "Transaction preferences updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(saveOrganisationPreferencesSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(saveOrganisationPreferencesFail(error));
    }
  };
};

export const updateXeroWalletRowAction = (payload: {
  walletId: string;
  preferences: {
    xero_account_id?: string;
    xero_tax_type?: string;
    xero_tracking_category_mapping?: { xero_tracking_category_id: string; xero_tracking_option_id?: string }[];
  };
}) => {
  return (dispatch: StoreDispatch) => {
    dispatch(updateXeroWalletRow(payload));
  };
};

export default xeroSlice.reducer;
